'use client';
import type { ConditionModel } from '@ctw/shared/api/fhir/models/condition';
import { PatientConditionOverview } from '@ctw/shared/content/conditions/patient-conditions-overview';
import { PatientDiagnosticReportsOverview } from '@ctw/shared/content/diagnostic-reports/patient-diagnostic-reports-overview';
import { EpisodeOfCareOverviewCard } from '@ctw/shared/content/episode-of-care/episode-of-care-overview-card';
import { PatientMedicationOverview } from '@ctw/shared/content/medications/patient-medications-overview';
import { PatientHistoryStatusCard } from '@ctw/shared/content/overview/zus-information-card';
import { PatientReferralsOverview } from '@ctw/shared/content/patient-referrals/patient-referrals-overview';
import { PatientRiskGapOverview } from '@ctw/shared/content/patient-risk-gaps/patient-risk-gaps-overview-card';
import { PatientVitalsOverview } from '@ctw/shared/content/vitals/patient-vitals-overview';
import type { CardType } from '@zeus-health/types';
import type React from 'react';
import type { OverviewResources } from './patient-overview';

export interface CardFactoryProps {
  onSeeAllResources: (resource: OverviewResources) => void;
  onAfterDetailsDrawerClosed?: () => void;
  onOpenDetailsDrawer?: (c: ConditionModel) => void;
  conditionIdForDetailsDrawer?: string;
}

export const getPatientOverviewCard = (
  cardType: CardType,
  index: number,
  props: CardFactoryProps,
): React.ReactElement | null => {
  const {
    onSeeAllResources,
    onAfterDetailsDrawerClosed,
    onOpenDetailsDrawer,
    conditionIdForDetailsDrawer,
  } = props;

  switch (cardType) {
    case 'conditions':
      return (
        <PatientConditionOverview
          key={`conditions-${index}`}
          onSeeAllResources={() => onSeeAllResources('conditions')}
          onAfterDetailsDrawerClosed={onAfterDetailsDrawerClosed}
          onOpenDetailsDrawer={onOpenDetailsDrawer}
          conditionIdForDetailsDrawer={conditionIdForDetailsDrawer}
        />
      );
    case 'risk-gaps':
      return (
        <PatientRiskGapOverview
          key={`risk-gaps-${index}`}
          onSeeAllResources={() => onSeeAllResources('risk-gaps')}
        />
      );
    case 'referrals':
      return (
        <PatientReferralsOverview
          key={`referrals-${index}`}
          onSeeAllResources={() => onSeeAllResources('referrals')}
        />
      );
    case 'medications':
      return (
        <PatientMedicationOverview
          key={`medications-${index}`}
          onSeeAllResources={() => onSeeAllResources('medications')}
        />
      );
    case 'diagnostics':
      return (
        <PatientDiagnosticReportsOverview
          key={`diagnostics-${index}`}
          onSeeAllResources={() => onSeeAllResources('diagnostics')}
        />
      );
    case 'episodesOfCare':
      return (
        <EpisodeOfCareOverviewCard
          key={`episodesOfCare-${index}`}
          onSeeAllResources={() => onSeeAllResources('encounters')}
        />
      );
    case 'vitals':
      return (
        <PatientVitalsOverview
          key={`vitals-${index}`}
          onSeeAllResources={() => onSeeAllResources('vitals')}
        />
      );
    case 'zusInformation':
      return <PatientHistoryStatusCard key={`zusInformation-${index}`} />;
    default:
      return null;
  }
};
